import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupsIcon from '@mui/icons-material/Groups';
import TimerIcon from '@mui/icons-material/Timer';
import SEO from '../components/SEO';

const Home = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const specialOffers = [
    {
      title: "Early Bird Discount",
      description: "Enroll before 30th June 2025 and get 5% off on annual fees",
      icon: <TimerIcon sx={{ fontSize: 40 }} />,
      chip: "Limited Time",
      color: "#4caf50"
    },
    {
      title: "Sibling Discount",
      description: "Get 10% off on fees when enrolling siblings together",
      icon: <GroupsIcon sx={{ fontSize: 40 }} />,
      chip: "Family Offer",
      color: "#2196f3"
    },
    {
      title: "Merit Scholarship",
      description: "Up to 15% scholarship for students with 90%+ in previous exams",
      icon: <EmojiEventsIcon sx={{ fontSize: 40 }} />,
      chip: "Merit Based",
      color: "#ff9800"
    }
  ];

  return (
    <Box>
      <SEO 
        title="SOLID CLASSES - Quality Education in Jodhpur"
        description="SOLID CLASSES offers comprehensive education for 6th to 12th standard students, including NEET-JEE preparation. Expert faculty, personalized attention, and proven track record."
        keywords="SOLID CLASSES, education, Jodhpur, NEET, JEE, coaching, physics, chemistry, mathematics, biology, best coaching in Jodhpur"
      />
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 4, md: 8 },
          mb: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            component="h1"
            variant={isMobile ? "h3" : "h2"}
            align="center"
            gutterBottom
            sx={{ fontWeight: 'bold' }}
          >
            Welcome to SOLID CLASSES
          </Typography>
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            align="center" 
            paragraph
            sx={{ px: { xs: 2, md: 0 } }}
          >
            Where Dreams Transform into Achievements
          </Typography>
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            align="center" 
            paragraph 
            sx={{ 
              mt: 2, 
              mb: 4, 
              fontWeight: 'bold', 
              color: '#4fc3f7',
              px: { xs: 2, md: 0 }
            }}
          >
            Admissions are open for 2025-2026 session!
          </Typography>
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button
              variant="contained"
              color="secondary"
              size={isMobile ? "medium" : "large"}
              onClick={() => navigate('/contact')}
              sx={{ px: { xs: 3, md: 4 } }}
            >
              Start Your Success Story
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Student Success Story / Testimonial Section */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 4, md: 6 }, mb: { xs: 4, md: 6 } }}>
        <Container maxWidth="md" sx={{ px: { xs: 1, md: 3 } }}>
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            align="center"
            gutterBottom
            sx={{
              color: theme.palette.mode === 'dark' ? '#ffffff' : 'primary.main',
              fontWeight: 'bold',
              mb: 3
            }}
          >
            Student Spotlight
          </Typography>
          <Card sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', p: { xs: 1, sm: 2, md: 3 }, boxShadow: 6, borderRadius: 3 }}>
            <Box sx={{ minWidth: 120, maxWidth: 180, width: { xs: '60%', sm: 180 }, display: 'flex', justifyContent: 'center', alignItems: 'center', p: { xs: 1, sm: 2 } }}>
              <img
                src={process.env.PUBLIC_URL + '/nikhil_solanki.png'}
                alt="Nikhil Solanki"
                style={{ width: '100%', height: 'auto', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}
              />
            </Box>
            <CardContent sx={{ flex: 1, pl: { sm: 3 }, pt: { xs: 2, sm: 0 } }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.mode === 'dark' ? '#4fc3f7' : 'primary.main' }} gutterBottom>
                Nikhil Solanki
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                कक्षा: 10वीं &nbsp; | &nbsp; प्रतिशत: 94.83%
              </Typography>
              <Typography variant="body2" paragraph sx={{ mb: 2, color: theme.palette.mode === 'dark' ? '#bbbbbb' : 'text.primary' }}>
                SOLID CLASSES में हम अपने छात्रों की उपलब्धियों पर गर्व महसूस करते हैं। निखिल सोलंकी की 10वीं बोर्ड परीक्षा में शानदार सफलता उनकी मेहनत और हमारे शिक्षकों के मार्गदर्शन का प्रमाण है। किशन सर के मार्गदर्शन में निखिल ने न केवल शैक्षणिक रूप से उत्कृष्ट प्रदर्शन किया, बल्कि भविष्य के लिए मजबूत नींव भी बनाई। हम मानते हैं कि हर छात्र में चमकने की क्षमता है, और हमारी व्यक्तिगत देखभाल हर छात्र को उसकी मंजिल तक पहुँचने में मदद करती है।
              </Typography>
              <Paper elevation={2} sx={{ p: 2, background: theme.palette.mode === 'dark' ? '#222' : '#f5f5f5', borderLeft: '4px solid #4fc3f7', mb: 2 }}>
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: theme.palette.mode === 'dark' ? '#e0e0e0' : 'text.secondary' }}>
                  "किशन सर का मार्गदर्शन वाकई शानदार था। उन्होंने कठिन से कठिन विषयों को भी बहुत आसान बना दिया और हमेशा मुझे आगे बढ़ने के लिए प्रेरित किया। SOLID CLASSES ने मुझे अपने सपनों को पूरा करने का आत्मविश्वास दिया।"
                </Typography>
                <Box sx={{ textAlign: 'right', mt: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.palette.mode === 'dark' ? '#4fc3f7' : 'primary.main' }}>
                    - निखिल सोलंकी
                  </Typography>
                </Box>
              </Paper>
              {/* भविष्य में और भी छात्रों की कहानियाँ यहाँ जोड़ी जा सकती हैं */}
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Mission Statement */}
      <Container maxWidth="lg" sx={{ mb: { xs: 4, md: 8 }, px: { xs: 2, md: 3 } }}>
        <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, textAlign: 'center' }}>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            gutterBottom 
            sx={{ 
              color: theme.palette.mode === 'dark' ? '#ffffff' : 'primary.main',
              fontWeight: 'bold'
            }}
          >
            Our Mission
          </Typography>
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            paragraph
            sx={{ 
              color: theme.palette.mode === 'dark' ? '#e0e0e0' : 'inherit'
            }}
          >
            "To empower every student with the knowledge, skills, and confidence to excel in their academic journey and beyond."
          </Typography>
          <Typography 
            variant="body1" 
            paragraph 
            sx={{ 
              px: { xs: 1, md: 4 },
              color: theme.palette.mode === 'dark' ? '#bbbbbb' : 'inherit'
            }}
          >
            At SOLID CLASSES, we believe that every student has the potential to achieve greatness. Our dedicated team of experienced educators is committed to nurturing this potential through personalized attention, innovative teaching methods, and a supportive learning environment.
          </Typography>
        </Paper>
      </Container>

      {/* Why Choose Us Section */}
      <Container maxWidth="lg" sx={{ mb: { xs: 4, md: 8 }, px: { xs: 2, md: 3 } }}>
        <Typography
          component="h2"
          variant={isMobile ? "h4" : "h3"}
          align="center"
          gutterBottom
          sx={{ 
            color: theme.palette.mode === 'dark' ? '#ffffff' : 'primary.main',
            fontWeight: 'bold'
          }}
        >
          Why Choose SOLID CLASSES?
        </Typography>
        <Grid container spacing={{ xs: 2, md: 4 }} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <SchoolIcon sx={{ fontSize: { xs: 32, md: 40 }, color: theme.palette.mode === 'dark' ? '#4fc3f7' : 'primary.main' }} />
                </Box>
                <Typography 
                  variant={isMobile ? "h6" : "h5"} 
                  component="h3" 
                  gutterBottom 
                  align="center"
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
                    fontWeight: 'bold'
                  }}
                >
                  Expert Faculty
                </Typography>
                <Typography 
                  align="center" 
                  variant="body2"
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#bbbbbb' : 'inherit'
                  }}
                >
                  Learn from experienced educators who are passionate about teaching and dedicated to your success. Our faculty members bring years of expertise and a deep understanding of competitive exams.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <EmojiEventsIcon sx={{ fontSize: { xs: 32, md: 40 }, color: theme.palette.mode === 'dark' ? '#4fc3f7' : 'primary.main' }} />
                </Box>
                <Typography 
                  variant={isMobile ? "h6" : "h5"} 
                  component="h3" 
                  gutterBottom 
                  align="center"
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
                    fontWeight: 'bold'
                  }}
                >
                  Proven Track Record
                </Typography>
                <Typography 
                  align="center" 
                  variant="body2"
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#bbbbbb' : 'inherit'
                  }}
                >
                  Our students consistently achieve outstanding results in board exams and competitive tests. Join the ranks of successful alumni who have realized their dreams through our guidance.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <GroupsIcon sx={{ fontSize: { xs: 32, md: 40 }, color: theme.palette.mode === 'dark' ? '#4fc3f7' : 'primary.main' }} />
                </Box>
                <Typography 
                  variant={isMobile ? "h6" : "h5"} 
                  component="h3" 
                  gutterBottom 
                  align="center"
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
                    fontWeight: 'bold'
                  }}
                >
                  Personalized Attention
                </Typography>
                <Typography 
                  align="center" 
                  variant="body2"
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#bbbbbb' : 'inherit'
                  }}
                >
                  Small batch sizes ensure individual attention and personalized guidance. We understand that every student learns differently, and we adapt our teaching methods accordingly.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Success Story Section */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 4, md: 8 }, mb: { xs: 4, md: 8 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
          <Typography 
            variant={isMobile ? "h4" : "h3"} 
            align="center" 
            gutterBottom 
            sx={{ 
              color: theme.palette.mode === 'dark' ? '#ffffff' : 'primary.main',
              fontWeight: 'bold'
            }}
          >
            Your Success is Our Priority
          </Typography>
          <Grid container spacing={{ xs: 2, md: 4 }} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
                <Typography 
                  variant={isMobile ? "h6" : "h5"} 
                  gutterBottom 
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#ffffff' : 'primary.main',
                    fontWeight: 'bold'
                  }}
                >
                  Comprehensive Learning Approach
                </Typography>
                <Typography 
                  variant="body2" 
                  paragraph
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#bbbbbb' : 'inherit'
                  }}
                >
                  • Regular assessments and progress tracking
                </Typography>
                <Typography 
                  variant="body2" 
                  paragraph
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#bbbbbb' : 'inherit'
                  }}
                >
                  • Doubt clearing sessions
                </Typography>
                <Typography 
                  variant="body2" 
                  paragraph
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#bbbbbb' : 'inherit'
                  }}
                >
                  • Practice tests and mock exams
                </Typography>
                <Typography 
                  variant="body2" 
                  paragraph
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#bbbbbb' : 'inherit'
                  }}
                >
                  • Study material and resources
                </Typography>
                <Typography 
                  variant="body2" 
                  paragraph
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#bbbbbb' : 'inherit'
                  }}
                >
                  • Parent-teacher meetings
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
                <Typography 
                  variant={isMobile ? "h6" : "h5"} 
                  gutterBottom 
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#ffffff' : 'primary.main',
                    fontWeight: 'bold'
                  }}
                >
                  Beyond Academics
                </Typography>
                <Typography 
                  variant="body2" 
                  paragraph
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#bbbbbb' : 'inherit'
                  }}
                >
                  • Career counseling and guidance
                </Typography>
                <Typography 
                  variant="body2" 
                  paragraph
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#bbbbbb' : 'inherit'
                  }}
                >
                  • Time management skills
                </Typography>
                <Typography 
                  variant="body2" 
                  paragraph
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#bbbbbb' : 'inherit'
                  }}
                >
                  • Stress management techniques
                </Typography>
                <Typography 
                  variant="body2" 
                  paragraph
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#bbbbbb' : 'inherit'
                  }}
                >
                  • Competitive exam preparation
                </Typography>
                <Typography 
                  variant="body2" 
                  paragraph
                  sx={{ 
                    color: theme.palette.mode === 'dark' ? '#bbbbbb' : 'inherit'
                  }}
                >
                  • Personality development
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Special Offers Section */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 4, md: 8 }, mb: { xs: 4, md: 8 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
          <Typography 
            variant={isMobile ? "h4" : "h3"} 
            align="center" 
            gutterBottom 
            sx={{ 
              color: theme.palette.mode === 'dark' ? '#ffffff' : 'primary.main',
              fontWeight: 'bold',
              mb: 4
            }}
          >
            Special Offers & Discounts
          </Typography>
          <Grid container spacing={{ xs: 2, md: 4 }} sx={{ mt: 2 }}>
            {specialOffers.map((offer, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6,
                    }
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, md: 3 }, textAlign: 'center' }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      mb: 2,
                      color: offer.color
                    }}>
                      {offer.icon}
                    </Box>
                    <Chip 
                      label={offer.chip}
                      sx={{ 
                        mb: 2,
                        backgroundColor: offer.color,
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                    <Typography 
                      variant={isMobile ? "h6" : "h5"} 
                      component="h3" 
                      gutterBottom 
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
                        fontWeight: 'bold'
                      }}
                    >
                      {offer.title}
                    </Typography>
                    <Typography 
                      variant="body2"
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? '#bbbbbb' : 'inherit',
                        mb: 2
                      }}
                    >
                      {offer.description}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => {
                        navigate('/fee-structure');
                        window.scrollTo(0, 0);
                      }}
                      sx={{ 
                        mt: 1,
                        backgroundColor: offer.color,
                        '&:hover': {
                          backgroundColor: offer.color,
                          opacity: 0.9
                        }
                      }}
                    >
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                color: theme.palette.mode === 'dark' ? '#bbbbbb' : 'inherit',
                mb: 2
              }}
            >
              * Terms and conditions apply. Contact us for more details.
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={() => {
                navigate('/fee-structure');
                window.scrollTo(0, 0);
              }}
              sx={{ 
                mt: 2,
                px: 4,
                '&:hover': {
                  transform: 'scale(1.05)',
                  transition: 'transform 0.3s ease-in-out'
                }
              }}
            >
              View Complete Fee Structure
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Call to Action */}
      <Container maxWidth="lg" sx={{ mb: { xs: 4, md: 8 }, px: { xs: 2, md: 3 } }}>
        <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{ fontWeight: 'bold' }}>
            Ready to Begin Your Journey to Success?
          </Typography>
          <Typography variant={isMobile ? "subtitle1" : "h6"} paragraph>
            Join SOLID CLASSES today and take the first step towards achieving your academic goals.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size={isMobile ? "medium" : "large"}
            onClick={() => navigate('/contact')}
            sx={{ mt: 2, px: { xs: 3, md: 4 } }}
          >
            Enroll Now
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default Home; 
